import { useCallback, useMemo, useState } from 'react';
import {
  MIN_PRESET_CHARTS,
  type CustomChartSize,
  type CustomChartTableView,
  type DashboardPreset,
  type DashboardPresetFilters,
} from '@/entities/dashboards';
import { useDashboardPresets } from '@/entities/settings';
import { stableStringify } from '@/shared/lib';
import { applyChartDrop, removeChartFromLayout, resizeChartColumn, type ChartDropPlace } from './chartDrop';
import {
  createEmptyChart,
  isResolvedChart,
  toChartDefinitions,
  toFormValues,
  withChartTemplate,
  type ChartAppearanceValues,
  type DashboardPresetFormValues,
  type PresetChartFormValues,
} from './dashboardPresetForm';

/** Правящийся дашборд: его состав и пресет, в который состав будет записан. */
export type DashboardDraft = DashboardPresetFormValues & {
  /** Пресет, состав которого правится; `null` — заводится новый дашборд */
  presetId: string | null;
};

/** Размер диаграммы: доля ширины сетки и высота. Не названная в правке величина остаётся прежней. */
export type ChartSizeChange = {
  size?: CustomChartSize;
  /** `null` — заданной высоты у диаграммы больше нет, она подбирается под содержимое */
  height?: number | null;
};

/**
 * Режим редактирования пользовательского дашборда: правка состава и свойств каждой диаграммы идёт
 * на самой сетке страницы, поэтому её состояние держит страница, а не окно.
 *
 * Правки копятся в черновике и в пресет попадают только сохранением — отказ от правок возвращает
 * дашборд к тому, каким он был до входа в режим. Описание каждой диаграммы истолковывается в её
 * свойствах, поэтому сохранению дашборда обращаться к модели уже не нужно.
 */
export const useDashboardPresetEditor = () => {
  const { presets, isUpdating, createPreset, updatePreset, apply } = useDashboardPresets();

  const [draft, setDraft] = useState<DashboardDraft | null>(null);
  // Диаграмма, свойства которой развёрнуты под ней; `null` — все свойства свёрнуты.
  const [expandedChartId, setExpandedChartId] = useState<string | null>(null);

  const startCreate = useCallback(() => {
    setDraft({ presetId: null, ...toFormValues(null) });
    setExpandedChartId(null);
  }, []);

  const startEdit = useCallback((preset: DashboardPreset) => {
    setDraft({ presetId: preset.id, ...toFormValues(preset) });
    setExpandedChartId(null);
  }, []);

  const cancel = useCallback(() => {
    setDraft(null);
    setExpandedChartId(null);
  }, []);

  const setName = useCallback((name: string) => {
    setDraft((current) => (current ? { ...current, name } : current));
  }, []);

  const updateCharts = useCallback((update: (charts: PresetChartFormValues[]) => PresetChartFormValues[]) => {
    setDraft((current) => {
      if (!current) return current;
      const charts = update(current.charts);
      // Тот же состав — тот же черновик: иначе растягивание давало бы ререндер на каждое
      // движение указателя, даже когда размер диаграммы не изменился.
      return charts === current.charts ? current : { ...current, charts };
    });
  }, []);

  /** Заменяет диаграмму её правкой; правка, вернувшая ту же диаграмму, состав не меняет. */
  const updateChart = useCallback(
    (chartId: string, update: (chart: PresetChartFormValues) => PresetChartFormValues) =>
      updateCharts((charts) => {
        const index = charts.findIndex((chart) => chart.chartId === chartId);
        if (index < 0) return charts;
        const updated = update(charts[index]);
        if (updated === charts[index]) return charts;
        const next = [...charts];
        next[index] = updated;
        return next;
      }),
    [updateCharts],
  );

  /**
   * Заводит диаграмму на месте, откуда её добавили: `afterIndex` — позиция диаграммы, за которой
   * встаёт новая, `startsRow` — место было отдельной строкой в конце сетки. Окна добавление не
   * открывает: под местом новой диаграммы разворачивается её же блок свойств, и описание
   * вводится в нём.
   */
  const addChart = useCallback(
    (afterIndex: number, startsRow: boolean) => {
      const chart = createEmptyChart(startsRow);
      updateCharts((charts) => {
        const at = afterIndex + 1;
        return [...charts.slice(0, at), chart, ...charts.slice(at)];
      });
      setExpandedChartId(chart.chartId);
    },
    [updateCharts],
  );

  /** Разворачивает свойства диаграммы под ней же; повторное обращение к той же — сворачивает. */
  const toggleChartProperties = useCallback(
    (chartId: string) => setExpandedChartId((current) => (current === chartId ? null : chartId)),
    [],
  );

  const collapseChartProperties = useCallback(() => setExpandedChartId(null), []);

  /**
   * Закрепляет за диаграммой введённое описание, не истолковывая его: свойства правятся не в один
   * заход, и набранный текст переживает сворачивание блока. Истолкование к этому времени уже
   * прошло — оно идёт потерей фокуса полем описания, — а не понятое описание остаётся текстом
   * при прежнем правиле.
   */
  const keepChartDescription = useCallback(
    (chartId: string, description: string) =>
      updateCharts((charts) => {
        const index = charts.findIndex((chart) => chart.chartId === chartId);
        // Описание прежнее или диаграмму уже убрали — состав тот же.
        if (index < 0 || charts[index].description === description) return charts;
        const kept = [...charts];
        kept[index] = { ...kept[index], description };
        return kept;
      }),
    [updateCharts],
  );

  /** Принимает правленые значения диаграммы: истолкованные на сетке или заданные в её свойствах. */
  const applyChartValues = useCallback(
    (edited: PresetChartFormValues) =>
      updateCharts((charts) => charts.map((chart) => (chart.chartId === edited.chartId ? edited : chart))),
    [updateCharts],
  );

  const applyChartProperties = useCallback(
    (edited: PresetChartFormValues) => {
      applyChartValues(edited);
      setExpandedChartId(null);
    },
    [applyChartValues],
  );

  /** Убирает диаграмму; колонка, из которой её убрали, сохраняет место в строке за следующей диаграммой. */
  const removeChart = useCallback(
    (chartId: string) => updateCharts((charts) => removeChartFromLayout(charts, chartId)),
    [updateCharts],
  );

  /**
   * Переставляет диаграмму на место, показанное при перетаскивании. Тем же перетаскиванием задаётся
   * и начало строки: отпущенная между строками диаграмма открывает собой новую, отпущенная внутри
   * строки встаёт в неё наравне с соседками.
   */
  const dropChart = useCallback(
    (chartId: string, place: ChartDropPlace) => updateCharts((charts) => applyChartDrop(charts, chartId, place)),
    [updateCharts],
  );

  /**
   * Ставит диаграмме оформление, заданное в её свойствах: цвета, правила раскраски и направление
   * элементов встают на сетке сразу, не дожидаясь сохранения свойств, — как и размер.
   */
  const restyleChart = useCallback(
    (chartId: string, appearance: ChartAppearanceValues) =>
      updateChart(chartId, (chart) => ({ ...chart, ...appearance })),
    [updateChart],
  );

  /**
   * Ставит диаграмме текст результата, набранный в её свойствах: правленый текст встаёт на сетке,
   * как только пользователь уходит из поля, — не дожидаясь сохранения свойств, как размер и оформление.
   */
  const retemplateChart = useCallback(
    (chartId: string, template: string | undefined) =>
      updateChart(chartId, (chart) => {
        const { rule } = chart;
        if (!rule) return chart;
        const retemplatedRule = withChartTemplate(rule, template);
        // Уход из поля без правки диаграмму не перестраивает: текст тот же — и состав тот же
        if (retemplatedRule.template === withChartTemplate(rule, rule.template).template) return chart;
        return { ...chart, rule: retemplatedRule };
      }),
    [updateChart],
  );

  /**
   * Ставит диаграмме размер. Доля ширины у диаграмм одной колонки общая, поэтому смена ширины любой
   * из них меняет ширину всей колонки; высота у каждой своя.
   */
  const resizeChart = useCallback(
    (chartId: string, change: ChartSizeChange) => {
      // Размер приходит на каждое движение указателя, а доля ширины меняется редко: пока размер
      // прежний, состав остаётся тем же.
      const { size, height } = change;
      if (size) updateCharts((charts) => resizeChartColumn(charts, chartId, size));
      if (height !== undefined) {
        updateChart(chartId, (chart) => (height === chart.height ? chart : { ...chart, height }));
      }
    },
    [updateCharts, updateChart],
  );

  /** Ставит столбчатой диаграмме ширину колонки названий, заданную перетаскиванием её границы. */
  const setChartLabelWidth = useCallback(
    (chartId: string, labelWidth: number) =>
      updateChart(chartId, (chart) => (chart.labelWidth === labelWidth ? chart : { ...chart, labelWidth })),
    [updateChart],
  );

  /**
   * Ставит таблице диаграммы вид колонок, настроенный на правящемся дашборде: он часть состава и
   * записывается сохранением режима; `null` — таблица показана всеми колонками выборки.
   */
  const setChartTableView = useCallback(
    (chartId: string, tableView: CustomChartTableView | null) =>
      updateChart(chartId, (chart) => ({ ...chart, tableView })),
    [updateChart],
  );

  /**
   * Записывает состав в пресет и выключает режим. Возвращает пресет дашборда: страница
   * показывает его вместо стандартного дашборда — правку пользователь вёл именно на нём.
   *
   * Совместный дашборд записывается на сервере: режим выключается по его ответу, а отказ
   * оставляет правки в режиме редактирования (`null`).
   *
   * @param filters - Фильтры панели: их получает новый дашборд, у правящегося фильтры не меняются
   */
  const save = useCallback(
    async (filters: DashboardPresetFilters): Promise<string | null> => {
      if (!draft) return null;
      const name = draft.name.trim();
      const charts = toChartDefinitions(draft.charts);
      let presetId = draft.presetId;
      if (presetId) {
        const isSaved = await updatePreset(presetId, { name, charts });
        if (!isSaved) return null;
        apply(presetId);
      } else {
        presetId = createPreset(name, charts, filters);
      }
      setDraft(null);
      setExpandedChartId(null);
      return presetId;
    },
    [draft, createPreset, updatePreset, apply],
  );

  /**
   * Уносит правки сохранённого дашборда в его личную копию и выключает режим: копия получает
   * правящийся состав с раскладкой и оформлением, а сам дашборд правок не получает. Копия не
   * закреплена и сразу применена. Возвращает копию; при заведении нового дашборда копировать
   * нечего — `null`.
   *
   * @param name - Название копии
   * @param filters - Фильтры панели, которые пользователь видит: их и получает копия
   */
  const saveAsCopy = useCallback(
    (name: string, filters: DashboardPresetFilters): string | null => {
      if (!draft?.presetId) return null;
      // Копия — свой состав, а не ссылки на правящийся и на фильтры дашборда
      const copyId = createPreset(name, structuredClone(toChartDefinitions(draft.charts)), structuredClone(filters));
      setDraft(null);
      setExpandedChartId(null);
      return copyId;
    },
    [draft, createPreset],
  );

  /**
   * Правки, ещё не записанные в пресет. Считаются сравнением с записанным составом, а не
   * отметкой на каждое действие: перетаскивание и растягивание шлют размер и порядок
   * непрерывно, в том числе когда диаграмма вернулась на прежнее место.
   *
   * Сравниваются значения состава, а не запись целиком: записанный дашборд возвращается из
   * хранилища с другим порядком полей, и посимвольное сравнение считало бы правкой любой вход
   * в режим редактирования.
   */
  const isDirty = useMemo(() => {
    if (!draft) return false;
    const saved = presets.find((preset) => preset.id === draft.presetId);
    return (
      stableStringify({ name: saved?.name ?? '', charts: saved?.charts ?? [] }) !==
      stableStringify({ name: draft.name.trim(), charts: toChartDefinitions(draft.charts) })
    );
  }, [draft, presets]);

  // Дашборду нужны название и хотя бы одна диаграмма — пустой состав показывать нечем.
  const canSave =
    !!draft && draft.name.trim().length > 0 && draft.charts.filter(isResolvedChart).length >= MIN_PRESET_CHARTS;

  return {
    draft,
    expandedChartId,
    isDirty,
    canSave,
    /** Состав записывается на сервере */
    isSaving: isUpdating,
    startCreate,
    startEdit,
    cancel,
    save,
    saveAsCopy,
    setName,
    addChart,
    toggleChartProperties,
    collapseChartProperties,
    keepChartDescription,
    applyChartValues,
    applyChartProperties,
    removeChart,
    dropChart,
    resizeChart,
    restyleChart,
    retemplateChart,
    setChartLabelWidth,
    setChartTableView,
  };
};

/** Состояние и действия режима редактирования пользовательского дашборда. */
export type DashboardPresetEditor = ReturnType<typeof useDashboardPresetEditor>;
